import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll } from 'framer-motion';
import { VideoPlayer } from './VideoPlayer';

interface ContentBlockProps {
  block: {
    type: 'image' | 'video' | 'text';
    src?: string;
    alt?: string;
    caption?: string;
    content?: string;
  };
  index: number;
}

export const ContentBlock: React.FC<ContentBlockProps> = ({ block, index }) => {
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isScrollingDown, setIsScrollingDown] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  useScroll();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrollingDown(currentScrollY > lastScrollY);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const isEven = index % 2 === 0;
  const alignmentContainerClass = isEven
    ? 'w-full flex justify-end mb-8'
    : 'w-full flex justify-start mb-8';

  const getMotionProps = () => {
    return {
      initial: { opacity: 0, y: isScrollingDown ? 50 : -50 },
      whileInView: { opacity: 1, y: 0 },
      viewport: { once: false, amount: 0.3, margin: "100px" },
      transition: { 
        duration: 0.7,
        ease: "easeOut"
      }
    };
  };

  const renderContent = () => {
    if (block.type === 'image') {
      return (
        <div className="w-full md:w-3/4 flex flex-col">
          <img
            src={block.src}
            alt={block.alt || block.caption || 'تصویر مرتبط با محتوا'}
            className="w-full rounded-2xl shadow-xl object-cover border border-white/10"
            loading="lazy"
          />
          {block.caption && (
            <p className="text-xs text-gray-400 mt-2 px-1 text-center md:text-right">{block.caption}</p>
          )}
        </div>
      );
    }

    if (block.type === 'video' && block.src) {
      return (
        <div className="w-full md:w-3/4">
          <VideoPlayer
            src={block.src}
            caption={block.caption}
            title={block.caption}
          />
        </div>
      );
    }

    if (block.type === 'text') {
      return (
        <div className="w-full bg-zinc-900/90 border border-white/10 text-gray-100 p-6 rounded-2xl shadow-xl">
          <p className="text-base md:text-lg leading-relaxed font-medium whitespace-pre-line">{block.content}</p>
        </div>
      );
    }

    return null;
  };

  return (
    <motion.div
      ref={ref}
      {...getMotionProps()}
      className={block.type === 'text' ? 'w-full mb-8' : alignmentContainerClass}
    >
      {renderContent()}
    </motion.div>
  );
};
